/**
 * Importar CCB como área do formulário (E56.F3.T02).
 *
 * Antes: um botão no canto do cabeçalho da página, com a dica alinhada à direita
 * e o resumo do que foi lido aparecendo depois, em outro bloco — o controle não
 * pertencia visualmente ao formulário que ele preenche, e era isso que o owner
 * relatou como "meio solto / layout quebrado" (item 2 da rodada 2).
 *
 * Agora é um objeto de blueprint dentro do `<form>`, com quatro estados
 * explícitos no mesmo lugar: vazio (o convite), lendo, importado (arquivo +
 * identificação lida) e erro (com o caminho de volta). Nada é persistido aqui —
 * a leitura é stateless no backend e a confirmação humana acontece nos campos.
 */
import { useRef, useState } from 'react';
import { FileText, Loader2, TriangleAlert, Upload } from 'lucide-react';

import { Blueprint, Button, CardKicker, MonoData } from '@/components/ui';
import { ptBR } from '@/i18n/pt-BR';
import { useApiClient } from '@/lib/api';
import {
  type CcbMetadataData,
  DocumentError,
  previewCcbMetadata,
} from '@/lib/case_source_documents';

const DOCX_MIME = 'application/vnd.openxmlformats-officedocument.wordprocessingml.document';

type Estado =
  | { kind: 'vazio' }
  | { kind: 'lendo' }
  | { kind: 'importado'; file: File; sbr: string | null }
  | { kind: 'erro'; mensagem: string };

interface CcbImportFieldProps {
  /** Chamado com os metadados propostos + o arquivo escolhido. */
  onImported: (metadata: CcbMetadataData, file: File) => void;
  disabled?: boolean;
}

function mensagemDeErro(err: unknown): string {
  if (err instanceof DocumentError && err.reason === 'unsupported_mime') {
    return ptBR.casos.importCcbErrorMime;
  }
  // F-E23.F3-03: contrato real do backend (400 DOCUMENT_UNREADABLE).
  if (err instanceof DocumentError && err.reason === 'unreadable') {
    return ptBR.casos.importCcbErrorUnreadable;
  }
  return ptBR.casos.importCcbErrorGeneric;
}

export function CcbImportField({ onImported, disabled }: CcbImportFieldProps): JSX.Element {
  const client = useApiClient();
  const inputRef = useRef<HTMLInputElement>(null);
  const [estado, setEstado] = useState<Estado>({ kind: 'vazio' });

  const lendo = estado.kind === 'lendo';

  async function handleFile(file: File) {
    if (file.type !== DOCX_MIME && !file.name.toLowerCase().endsWith('.docx')) {
      setEstado({ kind: 'erro', mensagem: ptBR.casos.importCcbErrorMime });
      return;
    }
    setEstado({ kind: 'lendo' });
    try {
      const metadata = await previewCcbMetadata(client, file);
      setEstado({ kind: 'importado', file, sbr: metadata.sbr_identification });
      onImported(metadata, file);
    } catch (err) {
      setEstado({ kind: 'erro', mensagem: mensagemDeErro(err) });
    }
  }

  return (
    <Blueprint as="section" data-testid="ccb-import" className="p-sc-6">
      <CardKicker className="block">{ptBR.casos.importCcbSectionTitle}</CardKicker>

      {/* O input fica fora de vista e quem o aciona é o botão; sem rótulo
          próprio ele ainda é exposto à tecnologia assistiva como campo sem nome
          (axe: `label`, crítica). O nome é o mesmo do botão que o dispara. */}
      <input
        ref={inputRef}
        type="file"
        accept=".docx"
        data-testid="import-ccb-input"
        aria-label={ptBR.casos.importCcbButton}
        className="sr-only"
        disabled={disabled || lendo}
        onChange={(e) => {
          const file = e.target.files?.[0];
          // Reseta o input para permitir re-selecionar o mesmo arquivo.
          e.target.value = '';
          if (file) void handleFile(file);
        }}
      />

      <div className="mt-sc-4 flex flex-wrap items-center gap-sc-4" data-state={estado.kind}>
        <CcbImportIcon estado={estado.kind} />
        <div className="min-w-[16rem] flex-1">
          <CcbImportTexto estado={estado} />
        </div>
        <Button
          type="button"
          variant="secondary"
          data-testid="import-ccb-button"
          disabled={disabled || lendo}
          onClick={() => inputRef.current?.click()}
        >
          {ccbCta(estado)}
        </Button>
      </div>
    </Blueprint>
  );
}

function ccbCta(estado: Estado): string {
  if (estado.kind === 'lendo') return ptBR.casos.importCcbLoading;
  if (estado.kind === 'importado') return ptBR.casos.importCcbReplace;
  if (estado.kind === 'erro') return ptBR.casos.importCcbRetry;
  return ptBR.casos.importCcbButton;
}

function CcbImportIcon({ estado }: { estado: Estado['kind'] }): JSX.Element {
  const cn = 'h-5 w-5 shrink-0 stroke-[1.5]';
  if (estado === 'lendo')
    return <Loader2 className={`${cn} animate-spin text-accent-700`} aria-hidden />;
  if (estado === 'importado') return <FileText className={`${cn} text-success-700`} aria-hidden />;
  if (estado === 'erro') return <TriangleAlert className={`${cn} text-danger-700`} aria-hidden />;
  return <Upload className={`${cn} text-fg-muted`} aria-hidden />;
}

function CcbImportTexto({ estado }: { estado: Estado }): JSX.Element {
  if (estado.kind === 'lendo') {
    return <p className="text-body-sm text-fg-muted">{ptBR.casos.importCcbLoading}</p>;
  }
  if (estado.kind === 'erro') {
    return (
      <p role="alert" data-testid="import-ccb-error" className="text-body-sm text-danger-700">
        {estado.mensagem}
      </p>
    );
  }
  if (estado.kind === 'importado') {
    return (
      <div data-testid="ccb-import-summary">
        <p className="text-body-sm text-success-700">{ptBR.casos.importCcbSuccess}</p>
        <p className="mt-1 text-caption text-fg-muted">
          {ptBR.casos.importCcbFileLabel}{' '}
          <MonoData className="text-fg">{estado.file.name}</MonoData>
        </p>
        {/* A ausência de identificação é dita, não omitida: o silêncio faria a
            leitura parecer completa quando ela não encontrou o número. */}
        {estado.sbr !== null ? (
          <p className="mt-1 text-caption text-fg-muted" data-testid="ccb-import-sbr">
            {ptBR.casos.importCcbSbrLabel} <MonoData className="text-fg">{estado.sbr}</MonoData>
          </p>
        ) : (
          <p className="mt-1 text-caption text-fg-dim" data-testid="ccb-import-sem-sbr">
            {ptBR.casos.importCcbNoSbr}
          </p>
        )}
      </div>
    );
  }
  return <p className="text-body-sm text-fg-muted">{ptBR.casos.importCcbEmptyHint}</p>;
}
